//
// Waitlist business rules — pure functions, no I/O.
//
// CAPACITY STATE IS DERIVED, NOT STORED.
//
// `RegistrationTeam.status` is the ADMIN-SET status ('' | 'accepted' |
// 'waitlisted' | 'withdrawn' — see ./registrationTeamStatus.ts). Two of its
// values are ADMIN OVERRIDES that this module honours before deriving anything:
//   - 'waitlisted' forces the team onto the waitlist (claims no seat)
//   - 'withdrawn'  removes it from the derivation entirely (no Map entry)
// `'accepted'` is still purely the admin lock-in and does NOT affect capacity —
// an accepted team can be secured, waitlisted or pending like any other. See
// CLAUDE.md and ADR 0027 for the full distinction.
//
// The capacity model (for teams with no admin override):
//   - Each division has N capacity spots (`division.config.capacity`,
//     0 = unlimited).
//   - A team only OCCUPIES a spot (secured or waitlist) if it has at least a
//     PARTIAL payment, OR the division is free ($0). See `occupiesCapacitySpot`.
//     A team with no payment (in a paid division) is `'pending'` — it holds
//     neither a secured nor a waitlist spot and isn't counted toward either.
//   - Each occupying team has a CAPACITY STATE — `'secured'` or `'waitlisted'`:
//     sort occupying teams by effective entry time (`promotedAt ?? created`)
//     ascending and award the first N=capacity a secured seat; the rest are
//     waitlisted.
//   - "Partial payment" = any received money: `hasPaid` (full) OR
//     `customPaidAmount > 0` (admin-recorded) OR a completed non-donation Stripe
//     payment > $0 (callers attach this as `paymentReceived`). `isFree` (a $0
//     resolved cost, caller-attached) also lets a team occupy a spot without
//     paying.
//   - MANUALLY-ADDED teams consume seats too (ADR 0056): a DivisionTeam with
//     `active: true` and no `registrationTeam` link (admin Add Team, imports)
//     holds a seat even though it never registered. Callers attach the COUNT of
//     such teams as `division.manualSeats` (same caller-attached convention as
//     `paymentReceived`/`isFree`). Manual teams seat FIRST — registrants FIFO
//     into the remaining `capacity - manualSeats` seats — and are folded into
//     `counts.secured` (plus exposed as `counts.manual`).
//
// Promotion (waitlisted → secured) is an event, not a derivation result:
// when a seat opens, an external caller (cron, deletion handler, etc.)
// must set `promotedAt = now` on the next FIFO waitlisted team. Pending
// (unpaid) teams are never waitlisted, so they are never promoted/emailed.
// `promotedAt`/the window now only orders promoted teams in the FIFO; the
// grace-window no longer SEATS unpaid teams.
//
// All time math takes an injectable `now` so callers can test or use a
// frozen timestamp.

import getObjId from './getObjId';
import { isWithdrawn, isForcedWaitlist } from './registrationTeamStatus';

export const DEFAULT_PAYMENT_WINDOW_HOURS = 72; // 3 days for original registrations
// Canonical home: configDefaults.ts (ADR 0035); re-exported for back-compat.
export { DEFAULT_PROMOTION_PAYMENT_WINDOW_HOURS } from './configDefaults';
import { DEFAULT_PROMOTION_PAYMENT_WINDOW_HOURS } from './configDefaults';

/**
 * 'withdrawn' is not really a capacity state — it means the team has been taken
 * OUT of the capacity system by an admin. It is in this union so callers that
 * ask about a single team get a truthful answer instead of a misleading
 * 'pending'; teams in this state never appear in the derived Map.
 */
export type CapacityState = 'secured' | 'waitlisted' | 'pending' | 'withdrawn';

export interface DivisionCapacityCounts {
  /** Teams holding one of the division's capacity spots (includes `manual`). */
  secured: number;
  /** Teams over capacity (and occupying a spot), waiting for a seat. */
  waitlisted: number;
  /** Subset of secured: teams that have fully paid. */
  paid: number;
  /** @deprecated grace seating was removed — always 0. Kept for back-compat. */
  unpaidInGrace: number;
  /** Teams with no payment (in a paid division): not secured, not waitlisted. */
  pending: number;
  /**
   * Manually-added teams (active DivisionTeams with no registrationTeam link,
   * caller-attached as `division.manualSeats` — ADR 0056). Already folded into
   * `secured`; exposed separately for transparency.
   */
  manual: number;
}

/**
 * The caller-attached count of manually-added teams (`division.manualSeats`),
 * sanitized: non-numeric/negative → 0.
 */
function getManualSeats(division: any): number {
  const n = Number(division?.manualSeats);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

// ── Payment state ─────────────────────────────────────────────────────

export function isPaidInFull(rt: any): boolean {
  return !!rt && !!rt.hasPaid;
}

/**
 * True if the team has received ANY payment: fully paid (`hasPaid`), an
 * admin-recorded amount (`customPaidAmount > 0`), a tolerated `amountPaid > 0`,
 * or a completed non-donation Stripe payment (caller-attached `paymentReceived`).
 */
export function hasPartialPayment(rt: any): boolean {
  if (!rt) return false;
  if (rt.hasPaid) return true;
  if (Number(rt.amountPaid) > 0) return true;
  if (Number(rt.customPaidAmount) > 0) return true;
  if (rt.paymentReceived) return true;
  return false;
}

/**
 * Whether a team is eligible to occupy a capacity spot (secured OR waitlist).
 * Free ($0 resolved cost, caller-attached `isFree`) teams qualify without
 * paying; everyone else needs at least a partial payment.
 */
export function occupiesCapacitySpot(rt: any): boolean {
  if (!rt) return false;
  if (rt.isFree) return true;
  return hasPartialPayment(rt);
}

// ── Payment clock ─────────────────────────────────────────────────────

/**
 * The team's effective entry time for FIFO ordering and grace-window math:
 * `promotedAt` if set (team was promoted off the waitlist), otherwise `created`.
 */
function getEffectiveEntryTime(rt: any): number {
  if (!rt) return Infinity;
  const candidates = [rt.promotedAt, rt.created];
  for (const c of candidates) {
    if (!c) continue;
    const d = c instanceof Date ? c : new Date(c);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  return Infinity;
}

export function getPaymentClockStart(rt: any): Date | null {
  const t = getEffectiveEntryTime(rt);
  return Number.isFinite(t) ? new Date(t) : null;
}

/**
 * Returns the payment grace window (in hours) that applies to a team.
 * Promoted teams (those with `promotedAt`) get the division's
 * `promotionPaymentWindowHours` (default 48). Original registrations get
 * `DEFAULT_PAYMENT_WINDOW_HOURS` (72).
 */
export function getPaymentWindowHours(rt: any, division: any): number {
  if (rt && rt.promotedAt) {
    const v = Number(division?.config?.promotionPaymentWindowHours);
    if (Number.isFinite(v) && v > 0) return v;
    return DEFAULT_PROMOTION_PAYMENT_WINDOW_HOURS;
  }
  return DEFAULT_PAYMENT_WINDOW_HOURS;
}

export function getPaymentDeadline(rt: any, division: any): Date | null {
  const start = getPaymentClockStart(rt);
  if (!start) return null;
  const hours = getPaymentWindowHours(rt, division);
  return new Date(start.getTime() + hours * 60 * 60 * 1000);
}

export function isPaymentOverdue(
  rt: any,
  division: any,
  now: Date | number = new Date(),
): boolean {
  if (!rt) return false;
  if (isPaidInFull(rt)) return false;
  const deadline = getPaymentDeadline(rt, division);
  if (!deadline) return false;
  const nowMs = typeof now === 'number' ? now : now.getTime();
  return nowMs > deadline.getTime();
}

// ── Division scope ────────────────────────────────────────────────────

/**
 * All teams competing for a given division's capacity — matched by each team's
 * single RESOLVED division: `division` when the admin has placed the team, else
 * `requestedDivision` while still pending placement. This mirrors how team
 * lists/badges attribute a team (`division._id || requestedDivision._id`), so a
 * team that was MOVED to another division counts only toward its new division —
 * not (double-counted) toward the division it originally requested. (The move
 * write-path intentionally keeps `requestedDivision` as the "Requested: X"
 * history, so matching on the union would over-count moved teams.)
 */
export function getRegistrationTeamsForDivision(divisionId: any, teams: any[] = []): any[] {
  if (!divisionId) return [];
  // Coerce to string: callers from the backend pass Mongoose ObjectId
  // instances (from .lean() queries), and ObjectId === ObjectId is always
  // false for different instances even when they wrap the same hex value.
  // Frontend callers already pass strings, so this is a no-op for them.
  const targetId = String(getObjId(divisionId) ?? '');
  if (!targetId) return [];
  return (teams || []).filter((rt) => {
    // Free agents (ADR 0054) never compete for a division seat — their
    // requestedDivision is only a PREFERENCE for the admin's assignment.
    // Excluding them HERE (the shared choke point both the public badges and
    // the server-side create gate flow through) is what keeps a 40-person
    // pool from marking an 8-team division "full".
    if (rt?.isFreeAgent) return false;
    const resolvedId = getObjId(rt?.division) ?? getObjId(rt?.requestedDivision);
    return resolvedId != null && String(resolvedId) === targetId;
  });
}

// ── Derivation ────────────────────────────────────────────────────────

/**
 * Compute the capacity state ('secured' | 'waitlisted' | 'pending') for every
 * team in a division. See the algorithm at the top of this file.
 *
 * `divisionTeams` should be all teams competing for this division (use
 * `getRegistrationTeamsForDivision` if you have a flat list across many
 * divisions). Each team should carry the payment signal (`hasPaid` /
 * `customPaidAmount` / caller-attached `paymentReceived`) and `isFree`.
 */
export function deriveCapacityStates(
  divisionTeams: any[] = [],
  division?: any,
  now: Date | number = new Date(),
): Map<string, CapacityState> {
  const states = new Map<string, CapacityState>();
  const capacity = Number(division?.config?.capacity || 0);

  // Admin overrides are applied BEFORE the unlimited-capacity shortcut below —
  // that shortcut marks everyone 'secured' and would otherwise quietly undo a
  // forced waitlist on any division without a capacity set.
  //
  // Withdrawn teams are dropped from the derivation entirely: no Map entry, so
  // getDivisionCapacityCounts skips them for free (it reads states.get(id)) and
  // they neither hold nor block a seat.
  const live: any[] = [];
  for (const t of divisionTeams) {
    const id = getObjId(t?._id);
    if (!id) continue;
    if (isWithdrawn(t)) continue;
    if (isForcedWaitlist(t)) {
      states.set(id, 'waitlisted');
      continue;
    }
    live.push(t);
  }

  // Unlimited capacity: no scarcity, everyone (not overridden above) is secured.
  if (!Number.isFinite(capacity) || capacity <= 0) {
    for (const t of live) {
      const id = getObjId(t?._id);
      if (id) states.set(id, 'secured');
    }
    return states;
  }

  // Sort by effective entry time ascending (FIFO).
  const sorted = [...live].sort(
    (a, b) => getEffectiveEntryTime(a) - getEffectiveEntryTime(b),
  );

  // Manually-added teams (ADR 0056) hold their seats before any registrant:
  // the admin placed them deliberately, so registrants FIFO into the remainder.
  let seatsClaimed = getManualSeats(division);
  for (const team of sorted) {
    const id = getObjId(team?._id);
    if (!id) continue;

    // No payment (and not free) → pending: holds no seat, not waitlisted.
    if (!occupiesCapacitySpot(team)) {
      states.set(id, 'pending');
      continue;
    }

    if (seatsClaimed >= capacity) {
      states.set(id, 'waitlisted');
      continue;
    }

    states.set(id, 'secured');
    seatsClaimed += 1;
  }

  return states;
}

export function getCapacityState(
  rt: any,
  divisionTeams: any[] = [],
  division?: any,
  now: Date | number = new Date(),
): CapacityState {
  const id = getObjId(rt?._id);
  if (!id) return 'pending';
  // Answer for a withdrawn team directly. It has no entry in the derived Map,
  // and the force-inject below would otherwise re-add it and report 'pending'.
  if (isWithdrawn(rt)) return 'withdrawn';
  // Ensure the team itself is part of the derivation set. A just-registered
  // team is often fetched separately from the division stats (getDivisionStats
  // may not yet reflect it — e.g. an unpaid team with only `requestedDivision`),
  // so it can be absent from `divisionTeams`. Without this, an unfound team fell
  // through to a 'waitlisted' default that wrongly HID Pay Now on an unlimited /
  // no-waitlist division (first-team-can't-pay). Deriving with the team included
  // yields its true state: 'secured' when capacity is unlimited, else 'pending'
  // (which still shows Pay Now) until it pays into a limited/full division.
  const teams = divisionTeams.some((t) => getObjId(t?._id) === id)
    ? divisionTeams
    : [...divisionTeams, rt];
  const states = deriveCapacityStates(teams, division, now);
  return states.get(id) ?? 'pending';
}

export function isSecured(
  rt: any,
  divisionTeams: any[] = [],
  division?: any,
  now: Date | number = new Date(),
): boolean {
  return getCapacityState(rt, divisionTeams, division, now) === 'secured';
}

/**
 * Reconcile waitlist-promotion notifications. Capacity state is derived and
 * auto-seats the next eligible team when a seat frees up, so "promotion" is no
 * longer a seating action — it's purely a NOTIFICATION event we must detect.
 *
 * Detecting the waitlisted → secured transition needs a tiny bit of history:
 * each team carries a persisted `wasWaitlisted` marker. Given a division's teams
 * (with the payment/free signals + `wasWaitlisted`), this returns:
 *   - `toMarkWaitlisted`: teams now 'waitlisted' that aren't flagged yet — the
 *     caller should set `wasWaitlisted = true`.
 *   - `toNotifyPromoted`: teams now 'secured' that WERE flagged waitlisted — the
 *     caller should email `waitlist_promoted` and clear `wasWaitlisted`.
 *
 * Idempotent: once a team is marked/notified, it won't reappear until its state
 * changes again. Does NOT touch `promotedAt` (setting it would reorder the FIFO
 * and could demote the just-promoted team).
 */
export function reconcileWaitlistNotifications(
  divisionTeams: any[] = [],
  division?: any,
  now: Date | number = new Date(),
): { toMarkWaitlisted: any[]; toNotifyPromoted: any[] } {
  const states = deriveCapacityStates(divisionTeams, division, now);
  const toMarkWaitlisted: any[] = [];
  const toNotifyPromoted: any[] = [];
  for (const team of divisionTeams) {
    const id = getObjId(team?._id);
    if (!id) continue;
    const state = states.get(id);
    if (state === 'waitlisted' && !team.wasWaitlisted) {
      toMarkWaitlisted.push(team);
    } else if (state === 'secured' && team.wasWaitlisted) {
      toNotifyPromoted.push(team);
    }
  }
  return { toMarkWaitlisted, toNotifyPromoted };
}

// ── Counts & queries ──────────────────────────────────────────────────

export function getDivisionCapacityCounts(
  divisionTeams: any[] = [],
  division?: any,
  now: Date | number = new Date(),
): DivisionCapacityCounts {
  const states = deriveCapacityStates(divisionTeams, division, now);
  let secured = 0;
  let waitlisted = 0;
  let paid = 0;
  let pending = 0;
  for (const team of divisionTeams) {
    const id = getObjId(team?._id);
    if (!id) continue;
    const state = states.get(id);
    if (state === 'secured') {
      secured += 1;
      if (isPaidInFull(team)) paid += 1;
    } else if (state === 'waitlisted') {
      waitlisted += 1;
    } else if (state === 'pending') {
      pending += 1;
    }
  }
  // Manual teams hold seats (they seeded the seat counter in the derivation),
  // so they belong in `secured` — every consumer of "how many spots are taken"
  // reads that field. `paid` stays registrant-only.
  const manual = getManualSeats(division);
  // unpaidInGrace is deprecated (grace seating removed) — always 0.
  return { secured: secured + manual, waitlisted, paid, unpaidInGrace: 0, pending, manual };
}

export function getAvailableSpots(division: any, divisionTeams: any[] = [], now: Date | number = new Date()): number {
  const capacity = Number(division?.config?.capacity || 0);
  if (!Number.isFinite(capacity) || capacity <= 0) return Infinity;
  const { secured } = getDivisionCapacityCounts(divisionTeams, division, now);
  return Math.max(0, capacity - secured);
}

export function isDivisionFull(division: any, divisionTeams: any[] = [], now: Date | number = new Date()): boolean {
  return getAvailableSpots(division, divisionTeams, now) === 0;
}

/**
 * Currently-waitlisted teams for a division, FIFO by effective entry time.
 * This is the order callers should email/promote when a spot opens.
 */
export function getWaitlistedTeams(
  divisionTeams: any[] = [],
  division?: any,
  now: Date | number = new Date(),
): any[] {
  const states = deriveCapacityStates(divisionTeams, division, now);
  return divisionTeams
    .filter((t) => states.get(getObjId(t?._id) as string) === 'waitlisted')
    .sort((a, b) => getEffectiveEntryTime(a) - getEffectiveEntryTime(b));
}
